<template>
  <div class="d-flex justify-content-center">
    <b-row :class="`mt-3 ${is_editing ? 'col-12 col-sm-10' : 'col-12 col-lg-8 col-xl-5'}`">
      <b-col class="col-12 text-center">
        <h1 class="mb-1">{{ $t('creator.addCover') }}</h1>
        <p>
          {{ $t('creator.coverAtention') }}
        </p>

        <div class="container-cover-upload">
          <div class="container-custom-input-file mb-2" v-if="!file_blob">
            <div class="container-file">
              <div class="mb-1"><span class="text-blue"><feather-icon icon="ImageIcon" class="mr-03"></feather-icon>Seleccionar</span> o arrastrar imagen de cover aquí</div>
              <p class="mb-0">Recomendado: imagen rectangular (16:9), menos de 5MB</p>
            </div>
            <b-form-file 
              class="custom-input" 
              v-model="file" 
              accept=".jpeg, .jpg, .png" 
              @input="onSelectFile"
            >
            </b-form-file>
          </div>
          <div class="container-blob-cover mb-1" v-else>
            <div class="container-actions">
              <div class="container-trash-blob" @click="deleteFile"> 
                <feather-icon icon="TrashIcon" class="icon-trash-blob"></feather-icon>
              </div>

              <div v-if="original_file" class="container-trash-blob" :title="$t('creator.cropCoverAdjust')" @click="show_cropper = true">
                <feather-icon icon="CropIcon" class="icon-trash-blob"></feather-icon>
              </div>
              
              <!-- Warning icon with popover -->
              <div v-if="imageDimensionsWarning" class="container-warning-blob" ref="warningIcon">
                <feather-icon icon="AlertTriangleIcon" class="icon-warning-blob"></feather-icon>
              </div>
            </div>
            
            <b-img
              :src="file_blob"
              :class="['img-blob-cover', {'is-croppable': original_file}]"
              :title="original_file ? $t('creator.cropCoverAdjust') : null"
              @click="original_file && (show_cropper = true)"
            ></b-img>
            
            <!-- Popover for dimensions warning -->
            <b-popover 
              v-if="imageDimensionsWarning"
              :target="() => $refs.warningIcon" 
              triggers="hover"
              placement="bottom"
              custom-class="popover-warning"
            >
              <div v-if="!isOptimalAspectRatio">
                <h5 class="title-popover">Proporción no ideal</h5>
                <p class="mb-1">Tu imagen no tiene proporción 16:9 ({{imageWidth}}x{{imageHeight}}px)</p>
                <p class="mb-1">Se recomienda una imagen rectangular</p>
                <p class="mb-1" v-if="original_file">{{ $t('creator.cropCoverText') }}</p>
              </div>
              <div v-if="fileSizeMB > 5">
                <h5 class="title-popover">Tamaño no ideal</h5>
                <p class="mb-1">Tu imagen pesa {{fileSizeMB}}MB</p>
                <p class="mb-0">Se recomienda menos de 5MB</p>
              </div>
            </b-popover>
          </div>
          <small v-if="file_blob && !original_file" class="d-flex align-items-center justify-content-center w-100 text-muted mb-1">
            <feather-icon icon="CropIcon" class="mr-03"></feather-icon>
            {{ $t('creator.cropCoverReupload') }}
          </small>
          <small class="mt-2 w-100 text-center text-muted">{{ $t('creator.coverRequired') }}</small>
        </div>

        <b-col class="col-12 d-flex justify-content-center" v-if="ready_prev_files">
          <step-attachment-files
            :invalid_size_file="fileSizeMB > 5"
            :cover="file"
            :main_color="this.palette_list.length > 0 ? this.palette_list.slice(0, 3).join(',') : '#8443a5'"
            :prev_files="prev_files"
            :blob_cover="file_blob"
          />
        </b-col>

      </b-col>
    </b-row>

    <canvas-color 
      :image="file" 
      v-if="file !== null && palette_list.length === 0" 
      :key="canvas_updated"
      @color_palette="getColorPalette"
    ></canvas-color>

    <cover-cropper
      v-if="show_cropper && original_file"
      :show_modal="show_cropper"
      :file="original_file"
      :initial_state="crop_state"
      @cropped="applyCrop"
      @keep_original="keepOriginal"
      @hidden="show_cropper = false"
    />
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormFile,
  BImg,
  BPopover
} from 'bootstrap-vue';
import utils from '@/libs/utils';
const loadToastificationContent = () => import('@core/components/toastification/ToastificationContent.vue');
import { getTempFiles } from '@/services/casting_calls';

export default {
  name: 'stepCoverImage',
  components: {
    BRow,
    BCol,
    BFormFile,
    BImg,
    BPopover,
    CanvasColor: () => import('@/views/pages/lists/CanvasColor.vue'),
    stepAttachmentFiles: () => import('./stepAttachmentFiles/stepAttachmentFiles.vue'),
    coverCropper: () => import('./coverCropper.vue'),
  },
  props: {
    is_editing: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      utils,
      preview: null,
      casting_call: {},
      steps: {},
      loading_service: false,
      loaded_contents: false,
      file: null,
      file_blob: null,
      imageDimensionsWarning: false,
      imageWidth: null,
      imageHeight: null,
      fileSizeMB: null,
      canvas_updated: false,
      palette_list: [],
      prev_cover_url: '',
      prev_files: [],
      ready_prev_files: false,
      original_file: null,
      show_cropper: false,
      crop_state: null,
    }
  },
  created() {
    this.getData();
  },
  computed: {
    has_slug() {
      return this.$route.params.slug;
    },
    isOptimalAspectRatio() {
      if (!this.imageWidth || !this.imageHeight) return true;
      const aspectRatio = this.imageWidth / this.imageHeight;
      const targetRatio = 16 / 9;
      const tolerance = 0.1;
      return Math.abs(aspectRatio - targetRatio) <= tolerance;
    },
  },
  methods: {
    showWarningToast({ title, text, showButton = false, textButton = null, onClick = null }) {
      loadToastificationContent().then((module) => {
        this.$toast({
          component: module.default,
          listeners: onClick ? { clickButton: onClick } : {},
          props: {
            title,
            icon: 'AlertTriangleIcon',
            text,
            variant: 'warning',
            showButton,
            textButton,
          },
        });
      });
    },
    validateSteps() {
      if (!this.steps?.brand && !this.has_slug) {
        this.showWarningToast({
          title: this.$t('campaigns.titleNoBrand'),
          text: this.$t('campaigns.textNoBrand'),
          showButton: true,
          textButton: this.$t('campaigns.goBrands'),
          onClick: this.redirectPreviewBrand,
        });
        return false;
      }

      if (!this.preview) {
        this.showWarningToast({
          title: this.$t('campaigns.titleNoCover'),
          text: this.$t('campaigns.textNoCover'),
        });
        return false;
      }

      if (!this.steps.title && !this.steps.castingIA && !this.has_slug) {
        const content_type = this.steps?.content_type?.value;
        const step_redirect = content_type === 'create_casting_ia' ? 'casting-ia' : 'title-casting-call';

        this.showWarningToast({
          title: this.$t('campaigns.titleNoData'),
          text: this.$t('campaigns.textNoData'),
          showButton: true,
          textButton: this.$t('campaigns.goStep'),
          onClick: () => {
            this.$router.push({
              name: 'steps_casting_call',
              params: { step: step_redirect },
            });
          },
        });
        return false;
      }

      return true;
    },
    redirectPreviewBrand() {
      this.$router.push({
        name: 'steps_casting_call',
        params: { step: 'organization-search' },
      })
    },
    getColorPalette(palette_list) {
      this.palette_list = palette_list;
    },
    onSelectFile() {
      // Se guarda la imagen original para poder volver a ajustarla desde cero
      this.original_file = this.file;
      this.crop_state = null;
      this.hasChangeFile(true);
    },
    keepOriginal() {
      this.show_cropper = false;
      this.crop_state = null;
      if (this.file !== this.original_file) this.replaceFile(this.original_file);
    },
    applyCrop(cropped_file, crop_state) {
      this.show_cropper = false;
      this.crop_state = crop_state;
      this.replaceFile(cropped_file);
    },
    replaceFile(new_file) {
      if (this.file_blob && this.file_blob.startsWith('blob:')) URL.revokeObjectURL(this.file_blob);
      this.file = new_file;
      this.palette_list = [];
      this.canvas_updated = !this.canvas_updated;
      this.hasChangeFile(false);
    },
    hasChangeFile(open_cropper = false) {
      if (this.file) {
        this.imageDimensionsWarning = false;
        
        const maxSize = 5 * 1024 * 1024;
        this.fileSizeMB = (this.file.size / (1024 * 1024)).toFixed(2);
        
        const img = new Image();
        img.onload = () => {
          this.imageWidth = img.width;
          this.imageHeight = img.height;
          
          const isNotOptimalRatio = !this.isOptimalAspectRatio;
          const isTooLarge = this.file.size > maxSize;
          
          if (isNotOptimalRatio || isTooLarge) {
            this.imageDimensionsWarning = true;
          }
          if (isNotOptimalRatio && open_cropper) this.show_cropper = true;
        };
        
        setTimeout(() => {
          this.file_blob = URL.createObjectURL(this.file);
          img.src = this.file_blob;
          this.preview = {preview: this.file_blob, uuid: this.casting_call.uuid || 'temp'};
        }, 200);
      }
    },
    getData() {
      const step = localStorage.getItem('steps')
      this.steps = step ? JSON.parse(step) : {}

      if (this.has_slug) {
        this.casting_call = this.$route.params.casting_call_data;
        this.file_blob = this.casting_call.cover_image ? utils.getAssetUrl(this.casting_call.cover_image) : this.casting_call.cover_url;
        this.preview = {
          preview: this.casting_call.cover_image ? utils.getAssetUrl(this.casting_call.cover_image) : this.casting_call.cover_url, uuid: this.casting_call.uuid
        }
        this.prev_files = this.$route.params.casting_call_data?.files || [];
        console.log(this.prev_files)
        this.ready_prev_files = true;
      } else {
        this.steps = JSON.parse(localStorage.getItem('steps'));
        this.getTempFiles();
      }
    },
    getTempFiles() {
      getTempFiles().then((response) => {
        if (response?.cover) this.file_blob = utils.isUrl(response.cover) ? response.cover : utils.getAssetUrl(response.cover);
        this.prev_files = response?.files || [];
        this.ready_prev_files = true;
      });
    },
    deleteFile() {
      this.preview = null;
      this.file = null;
      this.file_blob = null;
      this.imageDimensionsWarning = false;
      this.imageWidth = null;
      this.imageHeight = null;
      this.fileSizeMB = null;
      this.palette_list = [];
      this.original_file = null;
      this.crop_state = null;
    },
  },
}
</script>
<style lang="scss">
.custom-input {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 5;
  cursor: pointer;
  
  .custom-file-label {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border: none !important;
    background: transparent !important;
    cursor: pointer;
    margin: 0;
    color: transparent !important;
    font-size: 0 !important;
    
    &::after {
      display: none !important;
    }
    
    &::before {
      display: none !important;
    }
  }
  
  .custom-file-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
    border: none !important;
    outline: none !important;
    
    &:focus {
      box-shadow: none !important;
      border: none !important;
    }
  }
  
  // Ocultar cualquier elemento de bootstrap-vue
  .form-control {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  
  // Ocultar texto "No file chosen"
  input[type="file"] {
    color: transparent !important;
    background: transparent !important;
    border: none !important;
    opacity: 0 !important;
  }
  
  // Ocultar cualquier texto del label
  label {
    color: transparent !important;
    font-size: 0 !important;
    line-height: 0 !important;
  }
}
.popover-warning {
  background: #fff3cd !important;
  .arrow::after {
    border-left-color: #fff3cd !important;
  }
}
</style>

<style lang="scss" scoped>
.spinner-waiting-service {
  width: 18px;
  height: 18px;
}
.avatar-preview-sample {
  width: 45px;
  height: 45px;
  object-fit: cover;
  border-radius: 1em !important;
  margin: 0 1em 0 0;
}

.text-blue {
  color: #3483fa;
}
.mr-03 {
  margin-right: 0.3em;
}

.container-cover-upload {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.container-custom-input-file {
  position: relative;
  height: 200px;
  width: 100%;
  margin: 0 auto;
  border: 2px dashed #3483fa;
  border-radius: 0.5em;
  background: transparent;
  cursor: pointer;
  transition: border 0.3s ease;
  
  &:hover {
    border: 2px solid #3483fa;
  }
  
  .container-file {
    top: 0;
    left: 0;
    position: absolute;
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: rgba(0,0,0,.45);
    height: 200px;
    font-size: 11px;
    cursor: pointer;
    padding: 0.5em;
    text-align: center;
    pointer-events: none;
  }
}

.container-blob-cover {
  height: 200px;
  width: 100%;
  margin: 0 auto;
  background-color: #DEE2E6;
  border-radius: 0.7em;
  position: relative;
  
  .container-actions {
    position: absolute;
    top: 0.5em;
    right: 0.5em;
    display: flex;
    gap: 0.5em;
    z-index: 10;
  }
  
  .container-trash-blob {
    cursor: pointer;
    padding: 0.4em;
    border-radius: 10em;
    background-color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background-color: #bdc0c4da;
    }
    .icon-trash-blob {
      color: black;
      width: 16px;
      height: 16px;
    }
  }
  
  .container-warning-blob {
    cursor: pointer;
    padding: 0.4em;
    border-radius: 10em;
    background-color: #fff3cd;
    
    &:hover {
      background-color: #ffeaa7;
    }
    .icon-warning-blob {
      color: #856404;
      width: 16px;
      height: 16px;
    }
  }
  
  .img-blob-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.7em;

    &.is-croppable {
      cursor: pointer;
    }
  }
}
</style>